// Message semantic types, mirroring the server's messagingdomain.MessageType
// set. `type` says what a message MEANS; `content_type` says how its body is
// formatted. The two are orthogonal — a task assignment is `type` task_assignment
// with `content_type` text.
//
// Change = change two places: the server const block in
// packages/server-go/internal/core/domain/messaging/messaging.go and this file.

export const MESSAGE_TYPE_DEFAULT = 'default';
export const MESSAGE_TYPE_REPLY = 'reply';
export const MESSAGE_TYPE_TASK_ASSIGNMENT = 'task_assignment';
export const MESSAGE_TYPE_SYSTEM_JOIN = 'system_join';
export const MESSAGE_TYPE_SYSTEM_LEAVE = 'system_leave';
export const MESSAGE_TYPE_TASK_HEARTBEAT = 'task_heartbeat';

/**
 * Types rendered as a channel notice rather than as somebody's utterance:
 * membership changes the server narrates on a user's behalf.
 *
 * task_assignment is deliberately NOT here. It is also an event, but it carries
 * the task's own text and belongs in the thread's reading order, so it gets its
 * own presentation instead of the system-notice bubble.
 */
const SYSTEM_MESSAGE_TYPES = new Set<string>([
  MESSAGE_TYPE_SYSTEM_JOIN,
  MESSAGE_TYPE_SYSTEM_LEAVE,
]);

/**
 * messageType — an absent type reads as `default`. Messages written before the
 * column existed were backfilled to `default` by migration v16; treating a
 * missing value as anything else would render history as unknown events.
 */
export function messageType(type: string | null | undefined): string {
  return type ?? MESSAGE_TYPE_DEFAULT;
}

/**
 * isSystemMessage — a channel notice, by semantic type.
 *
 * The `sender_id === 'system'` fallback covers rows written before the type
 * column existed: migration v16 backfilled those to `default`, so their only
 * remaining marker is the sender sentinel. It cannot be dropped until that data
 * is gone (see #418).
 */
export function isSystemMessage(message: { type?: string | null; sender_id: string }): boolean {
  return SYSTEM_MESSAGE_TYPES.has(messageType(message.type)) || message.sender_id === 'system';
}

/**
 * isTaskAssignment — the announcement the server writes when a task is created.
 *
 * It is an event about the task, not its author speaking. Rendering it as a
 * normal bubble makes an agent that assigned a task to itself look like it is
 * talking to itself and then answering.
 */
export function isTaskAssignment(message: { type?: string | null }): boolean {
  return messageType(message.type) === MESSAGE_TYPE_TASK_ASSIGNMENT;
}

export function isTaskHeartbeat(message: { type?: string | null }): boolean {
  return messageType(message.type) === MESSAGE_TYPE_TASK_HEARTBEAT;
}
